import { useState, type FormEvent } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { FormField, Input } from './FormField';

export interface ReauthDialogProps {
  open: boolean;
  /** True while the previous submit is in flight - disables the form, shows a pending label. */
  pending: boolean;
  /** Set after a failed attempt (wrong password/code) - deliberately non-leaky per the server's
   * own message ("incorrect password or verification code", never which one was wrong). */
  error?: string;
  onCancel: () => void;
  onSubmit: (input: { password?: string; totpCode?: string }) => void;
}

/**
 * The shared re-auth dialog (used by apps/admin and apps/dashboard). Plain-language prompt, never
 * mentions "X-Reauth-Token" or any header name - the caller (an app-specific hook/provider) is
 * responsible for turning a server "re-auth required" response into opening this dialog, and for
 * retrying the original action once `onSubmit` succeeds server-side.
 *
 * The password and 2FA-code fields are both optional on the wire (the server accepts either one
 * alone - apps/api/src/routes/{admin,dashboard}/auth.ts's reauth route tries password first, then
 * falls back to the TOTP code), but the form requires at least one to be filled before it can be
 * submitted, so a user can't accidentally send an empty request.
 */
export function ReauthDialog({ open, pending, error, onCancel, onSubmit }: ReauthDialogProps) {
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!password && !totpCode) return;
    onSubmit({ password: password || undefined, totpCode: totpCode || undefined });
  }

  function handleCancel() {
    setPassword('');
    setTotpCode('');
    onCancel();
  }

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title="For your security, please confirm it's you"
      footer={
        <>
          <Button variant="secondary" onClick={handleCancel} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={pending || (!password && !totpCode)}
          >
            {pending ? 'Checking...' : 'Confirm'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <p className="text-sm text-ink-600 dark:text-ink-300">
          This action is sensitive, so we need to check it's really you before it goes through.
          Enter your password, or your 6-digit authenticator code if you have one handy.
        </p>
        {error && (
          <p role="alert" className="text-sm text-coral-600 dark:text-coral-400">
            {error}
          </p>
        )}
        <FormField label="Password">
          <Input
            type="password"
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={pending}
          />
        </FormField>
        <FormField
          label="Authenticator code or recovery code"
          hint="Only needed if you'd rather use this than your password - a 6-digit code from your app, or an XXXX-XXXX recovery code if you've lost it"
        >
          {/*
            REAL BUG FOUND AND FIXED (2FA rebuild): this used to be `inputMode="numeric"` with
            `maxLength={6}` and stripped every non-digit character on input - fine for a live TOTP
            code, but it made it IMPOSSIBLE to ever type a recovery code here at all (format
            `XXXX-XXXX`, from packages/auth/src/totp.ts's RECOVERY_CODE_ALPHABET - letters and a
            hyphen, not just digits). The server's own `/admin/auth/reauth` and
            `/dashboard/auth/reauth` routes already try a recovery code as a fallback when the
            TOTP check fails - this field just had no way to actually send one. "Self-reset via
            reauth with a code OR a recovery code" could never have worked for the recovery-code
            half through this dialog.
          */}
          <Input
            type="text"
            autoComplete="one-time-code"
            maxLength={9} // "XXXX-XXXX" (8 chars + hyphen) - the longest valid value this field sends
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.toUpperCase())}
            disabled={pending}
          />
        </FormField>
        {/* Submit button lives in the footer above; this hidden submit lets Enter-to-submit work. */}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}
